import React, { useState } from 'react';

import styles from './CreateChartModal.module.scss';
import { TpDataContext, TpDataTable, TpDataTableColumn } from '../../../data/interfaces';
import { getColumnInfo } from '../../../data/analyticsDataSlice';
import { useAppDispatch } from '../../../app/hooks';
import { chartFactory, chartTypeListForUserCreation } from '../charts/factory';
import { isCompatibleDataType } from '../widget/WidgetMenu';
import { DASHBOARDELEMIDS, TpChartDefinition, TpDashboardContext, TpWidgetContext } from '../dashboard/interfaces';
import { get_guid } from '../helpers/misc';
import { DashboardElementErrorBoundary } from '../dashboard/DashboardElementErrorBoundary';
import { TpChartManifest } from '../charts/interfaces';

function tryChart(
  ChartTypeInfo: TpChartManifest,
  dataTable: TpDataTable,
  columns: TpDataTableColumn[],
  config: any,
  scoredCharts: { chart: TpChartDefinition; score: number }[],
) {
  const usableAspects = ChartTypeInfo.aspects.filter((aspect) => aspect.quality && aspect.quality > 0);
  const usableAspectCount = usableAspects.length;

  function findFirstNonFinishedCol(columnsMapping: number[], aspectCount: number) {
    for (let i = 0; i < columnsMapping.length; i++) if (columnsMapping[i] < aspectCount - 1) return i;
    return null;
  }

  const columnsMapping = columns.map((id) => 0); // Hold the assignment of all used columns to aspect indexes. We begin by assigning them all to aspect 0

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // This loop iterates over all possible assignments of columnsMapping values to aspect indexes,
    // following the logic of a multi-digit counter, with each column a digit, and iterating over the number of aspects
    // It will stop when all columns are assigned to the last aspect

    // We skip iterations where several columns are assigned to the same aspect index
    const mapMap: any = {};
    let columnUsedMultipleTimes = false;
    for (const colMap of columnsMapping) {
      if (mapMap[colMap]) columnUsedMultipleTimes = true;
      mapMap[colMap] = true;
    }

    if (!columnUsedMultipleTimes) {
      // We try this chart type with the column to aspect id mapping present in the current columnsMapping
      const dataBinding: any = {
        dataTableId: dataTable.id,
        aspectBindings: {},
      };
      let score = 0;
      if (dataTable.defaultHoverLabel && ChartTypeInfo.aspects.map((aspect) => aspect.id).includes('hoverLabel')) {
        dataBinding.aspectBindings['hoverLabel'] = { columnId: dataTable.defaultHoverLabel };
      }
      let compatible = true;
      for (let colNr = 0; colNr < columns.length; colNr++) {
        if (columnsMapping[colNr] >= 0) {
          if (!isCompatibleDataType(usableAspects[columnsMapping[colNr]].dataType, columns[colNr].valueType))
            compatible = false;
          dataBinding.aspectBindings[usableAspects[columnsMapping[colNr]].id] = { columnId: columns[colNr].id };
          score += 0.5 ** colNr * (usableAspects[columnsMapping[colNr]].quality || 0);
        }
      }
      if (compatible) {
        // chart can show all selected columns
        let allRequiresAspectsMapped = true;
        for (const aspect of usableAspects)
          if (aspect.required) if (!dataBinding.aspectBindings[aspect.id]) allRequiresAspectsMapped = false;
        if (allRequiresAspectsMapped) {
          // chart does not require any additional columns
          scoredCharts.push({
            chart: {
              elemType: DASHBOARDELEMIDS.Widget,
              id: get_guid(),
              //elemLayoutHints: getLayoutInHorGroupRelative(1),
              chartType: ChartTypeInfo.id,
              dataBinding,
              config,
            },
            score,
          });
        }
      }
    }

    // This iterates to the next combination of column to aspect index assignment, similar to houw a multi-digit counter works
    const firstNonFinishedCol = findFirstNonFinishedCol(columnsMapping, usableAspectCount);
    if (firstNonFinishedCol !== null) {
      // we have a column that can be iterated further
      for (let i = 0; i < firstNonFinishedCol; i++) columnsMapping[i] = 0; // we reset all previous columns, similar to when a higher digit counter value is increased
      columnsMapping[firstNonFinishedCol]++;
    } else break; // we reached the end of all combination: all columns are assigned to the last aspect index
  }
}

function tryCompoundChart(
  ChartTypeInfo: TpChartManifest,
  dataTable: TpDataTable,
  columns: TpDataTableColumn[],
  config: any,
  scoredCharts: { chart: TpChartDefinition; score: number }[],
) {
  // For charts with compound aspects, we use a simpler, greedier logic
  const usableAspects = ChartTypeInfo.aspects.filter((aspect) => aspect.quality && aspect.quality > 0);
  const usableAspectCount = usableAspects.length;

  const dataBinding: any = {
    dataTableId: dataTable.id,
    aspectBindings: {},
    compoundAspectBindings: {},
  };
  const score = 1;

  let availableColumns = [...columns];
  // First, we match all required non-compound aspects
  for (const aspect of usableAspects)
    if (aspect.required) {
      const matchingColumn = availableColumns.find((col) => isCompatibleDataType(aspect.dataType, col.valueType));
      if (!matchingColumn) return; // chart does not work: a required aspect could not be matched to a column
      dataBinding.aspectBindings[aspect.id] = { columnId: matchingColumn.id };
      availableColumns = availableColumns.filter((col) => col.id != matchingColumn.id);
    }
  // Second, we match all compound aspect
  for (const aspect of ChartTypeInfo.compoundAspects!) {
    const binding = availableColumns
      .filter((col) => isCompatibleDataType(aspect.dataType, col.valueType))
      .map((col) => col.id);
    if (binding.length < aspect.minCount) return; // aspect cannot be bound to sufficient columns
    dataBinding.compoundAspectBindings[aspect.id] = binding;
    availableColumns = availableColumns.filter((col) => !isCompatibleDataType(aspect.dataType, col.valueType));
  }
  // Third, we match all non-required non-compound aspects
  for (const aspect of usableAspects)
    if (!aspect.required) {
      const matchingColumn = availableColumns.find((col) => isCompatibleDataType(aspect.dataType, col.valueType));
      if (matchingColumn) {
        dataBinding.aspectBindings[aspect.id] = { columnId: matchingColumn.id };
        availableColumns = availableColumns.filter((col) => col.id != matchingColumn.id);
      }
    }

  if (availableColumns.length > 0) return; // chart does not work: it could not use all chosen columns

  scoredCharts.push({
    chart: {
      elemType: DASHBOARDELEMIDS.Widget,
      id: get_guid(),
      chartType: ChartTypeInfo.id,
      dataBinding,
      config,
    },
    score,
  });
}

function getCompatibleCharts(
  dataContext: TpDataContext,
  dataTable: TpDataTable,
  columns: TpDataTableColumn[],
): [TpChartDefinition[], string | null] {
  const scoredCharts: { chart: TpChartDefinition; score: number }[] = [];

  let error: string | null = null;

  if (columns.length == 0)
    return [[], 'Please select one or more properties from the list to see charts that visualise those properties'];

  for (const chartTypeId of chartTypeListForUserCreation) {
    const ChartTypeInfo = chartFactory(chartTypeId);
    const config: any = {};
    for (const param of ChartTypeInfo.configParams) config[param.id] = param.paramType.defaultValue;
    if (ChartTypeInfo.compoundAspects) tryCompoundChart(ChartTypeInfo, dataTable, columns, config, scoredCharts);
    else tryChart(ChartTypeInfo, dataTable, columns, config, scoredCharts);
  }

  if (scoredCharts.length == 0) error = 'No chart can visualise all currently selected properties';
  scoredCharts.sort((a, b) => b.score - a.score);

  return [scoredCharts.map((item) => item.chart), error];
}

interface TpCreateChartModalProps {
  dataContext: TpDataContext;
  canonicalTypeId: string;
  dashboardContext: TpDashboardContext;
  close: () => void;
}

function createChart(
  dataContext: TpDataContext,
  dashboardContext: TpDashboardContext,
  chartDefinition: TpChartDefinition,
): any {
  const ChartManifest = chartFactory(chartDefinition.chartType);
  const widgetContext: TpWidgetContext = {
    dashboardContext,
    widgetId: get_guid(),
    zoomingActive: false,
    toggleSingleItemSelection: () => {},
    handleOpenItem: () => {},
    setBitmapDownloadFunction: () => {},
    handleUserSelectionAction: () => {},
  };
  return (
    <ChartManifest.component
      chartTypeManifest={ChartManifest}
      dataContext={dataContext}
      dataBinding={chartDefinition.dataBinding}
      config={chartDefinition.config}
      widgetContext={widgetContext}
    />
  );
}

export function CreateChartModal({
  dataContext,
  canonicalTypeId: canonicalTypeId,
  close,
  dashboardContext,
}: TpCreateChartModalProps) {
  const dispatch = useAppDispatch();

  const [activeColumns, setActiveColumns] = useState<string[]>([]);

  const compatibleTables = dataContext.dataTables.filter((table) => table.canonicalTypeId == canonicalTypeId);
  const dataTable = compatibleTables[0]; // @todo: we need something smarter here...

  const [charts, error] = getCompatibleCharts(
    dataContext,
    dataTable,
    activeColumns.map((colId) => getColumnInfo(dataTable, colId)),
  );

  // Render the chart previews for the compatible charts
  // No reset keys on the error boundary for the chart previews: user has no way to recover from errors in the previews
  const chartRenderings = charts.map((chartInfo, index) => (
    <div className={styles.previewChartWrapper} key={`idx_${index}`}>
      <DashboardElementErrorBoundary>
        {createChart(dataContext, dashboardContext, chartInfo)}
      </DashboardElementErrorBoundary>
      <div
        className={styles.previewChartlickLayer}
        onClick={() => {
          dashboardContext.addChart(chartInfo, dashboardContext.dashboardDefId);
          close();
        }}
      />
    </div>
  ));

  function toggleColumn(colId: string) {
    if (activeColumns.includes(colId)) {
      const columns = activeColumns;
      columns.splice(columns.indexOf(colId), 1);
      setActiveColumns([...columns]);
    } else {
      setActiveColumns([...activeColumns, colId]);
    }
  }

  function renderColumnActiveIndex(colId: string) {
    if (!activeColumns.includes(colId)) return null;
    return <div className={styles.columnNumberIcon}>{activeColumns.indexOf(colId) + 1}</div>;
  }

  function createColumnItem(column: TpDataTableColumn) {
    return (
      <div
        key={column.id}
        title={column.description || undefined}
        className={activeColumns.includes(column.id) ? styles.columnActive : styles.column}
        onClick={() => toggleColumn(column.id)}
      >
        <div className={styles.columnNumber}>{renderColumnActiveIndex(column.id)}</div>
        <div className={styles.columnDescription}>{column.name}</div>
        <span className={styles.dataTypeAnnotation}>{column.valueType}</span>
      </div>
    );
  }

  const columnsNotInGroups = dataTable.columns
    .filter((column) => !column.groupId)
    .map((column) => createColumnItem(column));

  // Iterate over column groups, filtering groups without any columns assigned to them
  const columnsInGroups = dataTable.columnGroups
    .filter((group) => dataTable.columns.some((column) => column.groupId == group.id))
    .map((group) => (
      <React.Fragment key={group.id}>
        <div className={styles.columnGroup}>{group.name}</div>
        {dataTable.columns.filter((column) => column.groupId == group.id).map((column) => createColumnItem(column))}
      </React.Fragment>
    ));

  return (
    <div className={styles.popup}>
      <div className={styles.columnList}>
        {columnsNotInGroups}
        {columnsInGroups}
      </div>
      <div className={styles.previewChartArea}>
        {error && <div className={styles.error}>{error}</div>}
        {!error && <div className={styles.previewChartAreaClient}>{chartRenderings}</div>}
      </div>
    </div>
  );
}
